import { useState } from "react";
import axios from "axios";
import { Copy, Eye, Pause, Play, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CT } from "@/lib/connectionsTheme";

const STYLE_LABELS = { standard: "Standard", branded: "Branded", auto: "Auto" };

export const QRCodeCard = ({ qr, canManage, onPreview, onChanged }) => {
  const { API } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    setError("");
    try {
      const action = qr.active ? "deactivate" : "reactivate";
      await axios.post(`${API}/org/intake/qr-codes/${qr.qr_id}/${action}`, {}, { withCredentials: true });
      onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the QR code "${qr.name}"? Printed/shared copies will stop working. This can't be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      await axios.delete(`${API}/org/intake/qr-codes/${qr.qr_id}`, { withCredentials: true });
      onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(qr.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`${CT.panel} p-4`} data-testid={`qr-card-${qr.qr_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900 dark:text-white" data-testid={`qr-card-name-${qr.qr_id}`}>{qr.name}</p>
          <p className="mt-0.5 truncate text-xs text-slate-500">{qr.intake_model_name}</p>
        </div>
        <span className={qr.active ? `${CT.badge} ${CT.badgeActive}` : `${CT.badge} ${CT.badgeMuted}`} data-testid={`qr-card-status-${qr.qr_id}`}>
          {qr.active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white p-4 dark:border-white/15">
        <img src={`${API}/org/intake/qr-codes/${qr.qr_id}/image?fmt=png`} alt={`QR code for ${qr.name}`} className="h-28 w-28" data-testid={`qr-card-thumbnail-${qr.qr_id}`} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
        <span data-testid={`qr-card-style-${qr.qr_id}`}>{STYLE_LABELS[qr.style] || qr.style}</span>
        <span data-testid={`qr-card-stats-${qr.qr_id}`}>{qr.scan_count} scans · {qr.submission_count} submissions</span>
      </div>

      {qr.style === "branded" && !qr.logo_available && (
        <p className="mt-2 text-xs font-semibold text-amber-500" data-testid={`qr-card-no-logo-notice-${qr.qr_id}`}>
          No organization logo set — rendering as Standard.
        </p>
      )}

      {error && <p className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400" data-testid={`qr-card-error-${qr.qr_id}`}>{error}</p>}

      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={onPreview} data-testid={`qr-card-preview-btn-${qr.qr_id}`} className={CT.btnSecondary}>
          <Eye size={12} /> Preview
        </button>
        <button type="button" onClick={copyLink} data-testid={`qr-card-copy-btn-${qr.qr_id}`} className={CT.btnSecondary}>
          <Copy size={12} /> {copied ? "Copied!" : "Copy Link"}
        </button>
        {canManage && (
          <button type="button" onClick={toggleActive} disabled={busy} data-testid={`qr-card-toggle-btn-${qr.qr_id}`} className={CT.btnIcon} title={qr.active ? "Deactivate" : "Reactivate"}>
            {qr.active ? <Pause size={13} /> : <Play size={13} />}
          </button>
        )}
        {canManage && (
          <button type="button" onClick={handleDelete} disabled={busy} data-testid={`qr-card-delete-btn-${qr.qr_id}`} className="inline-flex items-center justify-center rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10" title="Delete QR Code">
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
};
